import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const Safety: React.FC<{ content?: any }> = ({ content }) => {
  const { t_api } = useLanguage();

  const title = content?.title || t_api("Health, Safety & Environment (QHSE)", "الصحة والسلامة والبيئة (QHSE)");
  const descTop = content?.descTop || t_api("Whilst Quality, Health, Safety and Environmental considerations (QHSE) form part of our everyday lives, HTD strongly focusses on this also being part of our working lives. Staff welfare and a positive safety culture are very close to our hearts, this enforced from the Senior Management Team down and embraced by all.", "في حين أن اعتبارات الجودة والصحة والسلامة والبيئة (QHSE) تشكل جزءًا من حياتنا اليومية، تركز HTD بقوة على أن يكون هذا أيضًا جزءًا من حياتنا العملية. إن رفاهية الموظفين وثقافة السلامة الإيجابية أمران قريبان جدًا من قلوبنا.");
  const descMid = content?.descMid || t_api("How do we ensure our “One Team, One Goal” moto is effectively communicated, we adopt a route and branch approach, comprising of:", "كيف نضمن توصيل شعارنا \"فريق واحد، هدف واحد\" بشكل فعال، ونحن نعتمد نهجًا للطريق والفرع، يتكون من:");
  const points = content?.points || [
    t_api("Recruitment selection and employment", "اختيار التوظيف والتوظيف"),
    t_api("Robust internal Induction Training and orientation", "تدريب وتوجيه داخلي قوي"),
    t_api("Job Descriptions; Policies and Procedures", "الأوصاف الوظيفية؛ السياسات والإجراءات"),
    t_api("Mandatory Customer and industry training", "التدريب الإلزامي للعملاء والصناعة"),
    t_api("Regular Competency Assessments", "تقييمات الكفاءة المنتظمة"),
    t_api("Supervision and coaching", "الإشراف والتدريب"),
    t_api("Strong leadership and monitoring", "قيادة ومراقبة قوية")
  ];
  const descBottom = content?.descBottom || t_api("Underpinning and supporting our Culture and methodology are our ISO Standards, HTD have held ISO 9001:2015; ISO14001:2015 and ISO45001:2018 accreditation since 2022, quality doctrine underpins our operational execution.\n\nHowever, we must forget our Staff, a diverse family that we need to consider at all times, operating in challenging conditions. The use of dynamic dashboards accessible via multiple locations with role-based user accounts, training material designed in a suitable language and format, training delivered at the correct levels and at a pace that ensures it's understood, all help HTD to ensure we do not sacrifice safety for speed!", "إن معايير ISO الخاصة بنا تدعم وتدعم ثقافتنا ومنهجيتنا، وقد حصلت HTD على شهادة ISO 9001:2015؛ اعتماد ISO14001:2015 وISO45001:2018 منذ عام 2022، تدعم مبادئ الجودة تنفيذنا التشغيلي.");

  const images = content?.images || [
    "/health/hs (1).jpg",
    "/health/hs 2.jpg",
    "/health/hs3.jpg",
    "/health/hs4.jpg",
  ];

  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-20">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight"
          >
            {title}
          </motion.h2>
          <div className="w-20 h-1 bg-red-600 mx-auto mt-6 rounded-full" />
        </div>

        {/* Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <p className="text-slate-600 text-[16px] md:text-[18px] leading-[1.8] text-justify font-medium">
                {descTop}
              </p>
              
              {descMid && (
                <p className="text-slate-800 text-[15px] md:text-[17px] font-bold leading-[1.8]">
                  {descMid}
                </p>
              )}

              {points && points.length > 0 && (
                <div className="space-y-3.5 pl-1">
                  {points.map((pt: string, idx: number) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-[15px] font-medium leading-relaxed">{pt}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-6 pt-2">
                {(descBottom || "").split('\n\n').map((para: string, idx: number) => (
                  <p key={idx} className="text-slate-600 text-[15px] md:text-[17px] leading-[1.8] text-justify font-medium">
                    {para}
                  </p>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right - 2x2 Image Grid */}
          <motion.div
            className="grid grid-cols-2 gap-6 sticky top-32"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            {images.slice(0, 4).map((src: string, index: number) => (
              <div key={index} className={`aspect-[4/3] rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white ${index % 2 === 1 ? 'translate-y-8' : ''} group`}>
                <img
                  src={src}
                  alt={`QHSE ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Safety;
